import { useState, useEffect, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Apple, Loader2, Search, Info } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

export default function ProduceTodayPriceDialog({ open, onOpenChange }) {
  const [sum, setSum] = useState(null);
  const [limit, setLimit] = useState(40);
  const [auto, setAuto] = useState(false);
  const [starting, setStarting] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/products/produce-prices/summary"); setSum(data); return data; }
    catch { return null; }
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);

  useEffect(() => {
    if (!open || !sum?.running) return;
    const t = setInterval(load, 3500);
    return () => clearInterval(t);
  }, [open, sum?.running, load]);

  const start = async () => {
    setStarting(true);
    try {
      const { data } = await api.post("/products/produce-prices/run", { limit: Number(limit) || 40, auto_continue: auto });
      if (data.started) { toast.success(data.message || "Araştırma başladı"); await load(); }
      else toast.info(data.message || "Araştırılacak ürün yok");
    } catch (e) { toast.error(e?.response?.data?.detail || "Başlatılamadı"); }
    finally { setStarting(false); }
  };

  const cancel = async () => {
    try { await api.post("/products/produce-prices/cancel"); toast.info("İptal istendi — mevcut ürün bitince duracak"); await load(); }
    catch { toast.error("İptal edilemedi"); }
  };

  const pct = sum?.job_total ? Math.round((sum.done / sum.job_total) * 100) : 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="produce-price-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading"><Apple className="h-5 w-5 text-emerald-600" /> Manav — Bugünün Fiyatı</DialogTitle>
          <DialogDescription>Taze meyve/sebzenin bugünkü PERAKENDE piyasa fiyatını Google AI ile çok kaynaktan (zincir/online market, semt pazarı) araştırıp kıyaslar. Hal fiyatı değildir; her araştırma LLM kredisi harcar.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-2 text-center">
            <div className="rounded-md bg-slate-50 p-2.5 ring-1 ring-inset ring-slate-200">
              <p className="text-[10px] uppercase tracking-wide text-slate-400">Manav Ürünü</p>
              <p className="tabular text-lg font-bold text-slate-900" data-testid="produce-total">{sum?.total ?? "—"}</p>
            </div>
            <div className="rounded-md bg-emerald-50 p-2.5 ring-1 ring-inset ring-emerald-200">
              <p className="text-[10px] uppercase tracking-wide text-emerald-600">Bugün Güncel</p>
              <p className="tabular text-lg font-bold text-emerald-700" data-testid="produce-today">{sum?.priced_today ?? "—"}</p>
            </div>
            <div className="rounded-md bg-amber-50 p-2.5 ring-1 ring-inset ring-amber-200">
              <p className="text-[10px] uppercase tracking-wide text-amber-600">Güncellenecek</p>
              <p className="tabular text-lg font-bold text-amber-700" data-testid="produce-pending">{sum ? (sum.total - sum.priced_today) : "—"}</p>
            </div>
          </div>

          {sum?.low_confidence > 0 && (
            <div data-testid="produce-lowconf" className="flex items-center gap-1.5 rounded-md bg-red-50 px-2.5 py-1.5 text-[11px] text-red-700 ring-1 ring-inset ring-red-100">
              <Info className="h-3.5 w-3.5 shrink-0" /> {sum.low_confidence} üründe düşük güvenli fiyat — kartlarda “düşük · incele” etiketli; elle kontrol edin.
            </div>
          )}

          {sum?.running ? (
            <div className="space-y-2" data-testid="produce-progress">
              <div className="flex items-center justify-between text-xs text-slate-500">
                <span className="flex items-center gap-1.5">Araştırılıyor…{sum.auto_continue && <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">Otomatik devam</span>}</span>
                <span className="tabular">{sum.done}/{sum.job_total}{sum.failed ? ` · ${sum.failed} bulunamadı` : ""}</span>
              </div>
              <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100"><div className="h-full rounded-full bg-emerald-500 transition-all" style={{ width: `${pct}%` }} /></div>
              <Button data-testid="produce-cancel-btn" onClick={cancel} variant="outline" size="sm" className="w-full border-red-300 text-red-600 hover:bg-red-50">İptal Et</Button>
              <p className="text-[11px] text-slate-400">Bu pencereyi kapatabilirsiniz; arka planda sürer.</p>
            </div>
          ) : (
            <>
              <div>
                <label className="text-xs font-semibold text-slate-600">Bu turda kaç ürün? (kredi kontrolü)</label>
                <select data-testid="produce-limit-select" value={limit} onChange={(e) => setLimit(Number(e.target.value))}
                  className="mt-1 h-10 w-full rounded-md border border-input bg-white px-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500">
                  {[20, 40, 60].map((n) => <option key={n} value={n}>{`${n} ürün`}</option>)}
                </select>
              </div>
              <label data-testid="produce-auto-continue" className="flex cursor-pointer items-start gap-2 rounded-md bg-emerald-50/60 p-2.5 ring-1 ring-inset ring-emerald-100">
                <input type="checkbox" checked={auto} onChange={(e) => setAuto(e.target.checked)} className="mt-0.5 h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500" />
                <span className="text-[12px] text-slate-600"><b className="text-slate-800">Bitince otomatik devam et</b> — tüm manav ürünleri bugün güncellenene kadar sıradaki grupları kendiliğinden araştırır (kredi harcar).</span>
              </label>
              {sum?.finished_at && sum?.job_total > 0 && (
                <p className="text-[11px] text-emerald-600" data-testid="produce-last-result">Son iş: {sum.done}/{sum.job_total} güncellendi{sum.failed ? `, ${sum.failed} bulunamadı` : ""}.</p>
              )}
              <div className="flex items-start gap-1.5 rounded-md bg-sky-50 p-2 text-[11px] text-sky-700 ring-1 ring-inset ring-sky-100">
                <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" /> Fiyatlar çok kaynaklı araştırma tahminidir — hal fiyatı değil, gerçek perakende raf fiyatı hedeflenir. Kesin fiyatı elle de girebilirsiniz.
              </div>
              <Button data-testid="produce-start-btn" onClick={start} disabled={starting || (sum?.total ?? 0) === 0}
                className="w-full bg-emerald-600 text-white hover:bg-emerald-700">
                {starting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Search className="mr-2 h-4 w-4" />} Bugünün Fiyatını Araştır
              </Button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
